/**
 * Admin → Recruiter Teams tab — feature E05-F05-T04.
 *
 * 1:1 port of the legacy `dashboard.js` recruiter UI (loadTeams / renderTeams /
 * openTeamDialog / saveTeam / deleteTeam) + `index.html` #admin-content-teams. It
 * is the admin Settings tab that LISTS recruiter teams as cards and offers CRUD;
 * a team carries a Team Lead (picked from the users list, role!=='system') and a
 * READ-ONLY derived members list (membership is assigned via the Users tab —
 * users.team_id = team NAME).
 *
 * Card (renderTeams, T04 acceptance #3):
 *   - heading: the team name;
 *   - a Team Lead line with a crown: the joined `teamlead_name`, or "—" when no
 *     lead is assigned (teamLeadLabel, teamsModel);
 *   - member chips (read-only) or the legacy "No members assigned" placeholder;
 *   - Edit + Delete actions;
 *   - empty state: "No teams configured".
 *
 * Dialog (openTeamDialog / saveTeam):
 *   - Team Name * + a Team Lead `<select>` whose options are the users EXCLUDING
 *     role==='system' (teamLeadOptions, teamsModel);
 *   - on Edit the members are shown read-only ("Assign users via Users tab" when
 *     none) — the dialog never edits membership;
 *   - a blank name shows the toast "Name is required" (legacy showNotification).
 *
 * Delete is a HARD delete (the API physically removes the row) — the behavioural
 * difference from the Sourcing Teams tab (SOFT delete). Transport: useTeams /
 * useCreateTeam / useUpdateTeam / useDeleteTeam (teamsApi.ts) + useUsers
 * (usersApi.ts, for the teamlead picker) → the shared apiClient.
 */
import { useMemo, useState } from 'react';
import type { TeamDTO } from '@eer/shared';
import { Dialog, DialogFooter, useToast } from '../ui/index.js';
import { teamLeadLabel, teamLeadOptions } from './teamsModel.js';
import { errorMessage } from '../api/client.js';
import { useTeams, useCreateTeam, useUpdateTeam, useDeleteTeam } from './teamsApi.js';
import { useUsers } from './usersApi.js';

const FIELD_CLASS =
  'w-full rounded border border-border px-3 py-2 text-sm focus:border-primary focus:outline-none';

// ---------------------------------------------------------------------------
// Add / Edit dialog (#team-overlay)
// ---------------------------------------------------------------------------

interface RecruiterTeamDialogProps {
  /** The team being edited, or null for an Add. */
  readonly team: TeamDTO | null;
  readonly onClose: () => void;
}

function RecruiterTeamDialog({ team, onClose }: RecruiterTeamDialogProps): React.JSX.Element {
  const toast = useToast();
  const usersQuery = useUsers();
  const createTeam = useCreateTeam();
  const updateTeam = useUpdateTeam();
  const [name, setName] = useState(() => team?.name ?? '');
  const [teamleadId, setTeamleadId] = useState<string>(() => team?.teamlead_id ?? '');

  const isEdit = team != null;
  const pending = createTeam.isPending || updateTeam.isPending;

  // Team-lead options: every user EXCLUDING the service user (role==='system').
  const leadOptions = useMemo(() => teamLeadOptions(usersQuery.data ?? []), [usersQuery.data]);

  function handleSave(): void {
    const trimmed = name.trim();
    // Legacy: `if (!name) { showNotification('Name is required', 'error'); return; }`
    if (!trimmed) {
      toast.error('Name is required');
      return;
    }
    const lead = teamleadId || null;
    const onSuccess = (): void => {
      toast.success('Team saved');
      onClose();
    };
    const onError = (error: unknown): void => {
      toast.error(errorMessage(error, 'Save failed'));
    };

    if (isEdit && team) {
      updateTeam.mutate(
        { id: team.id, patch: { name: trimmed, teamlead_id: lead } },
        { onSuccess, onError },
      );
    } else {
      createTeam.mutate({ name: trimmed, teamlead_id: lead }, { onSuccess, onError });
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title={isEdit ? 'Edit Team' : 'Add Team'}
      maxWidth="md"
      testId="team-dialog"
      footer={
        <DialogFooter
          onCancel={onClose}
          onConfirm={handleSave}
          confirmLabel="Save"
          confirmDisabled={pending}
          testId="team-dialog-footer"
        />
      }
    >
      <div className="grid gap-4">
        <label htmlFor="team-edit-name" className="block">
          <span className="mb-1 block text-xs font-medium text-text-strong">Team Name *</span>
          <input
            id="team-edit-name"
            data-testid="team-edit-name"
            type="text"
            className={FIELD_CLASS}
            value={name}
            placeholder="e.g. Team EMEA"
            onChange={(e) => setName(e.target.value)}
          />
        </label>

        <label htmlFor="team-edit-lead" className="block">
          <span className="mb-1 block text-xs font-medium text-text-strong">Team Lead</span>
          <select
            id="team-edit-lead"
            data-testid="team-edit-lead"
            className={FIELD_CLASS}
            value={teamleadId}
            onChange={(e) => setTeamleadId(e.target.value)}
          >
            <option value="">— Select —</option>
            {leadOptions.map((opt) => (
              <option key={opt.id} value={opt.id}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>

        {/* Members are READ-ONLY in this dialog — membership is set via Users tab. */}
        {isEdit ? (
          <div>
            <span className="mb-1 block text-xs font-medium text-text-strong">Members</span>
            <div data-testid="team-dialog-members" className="flex flex-wrap gap-1">
              {(team?.members ?? []).length > 0 ? (
                (team?.members ?? []).map((m) => (
                  <span
                    key={m.id}
                    className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-600"
                  >
                    {m.name}
                  </span>
                ))
              ) : (
                <span className="text-xs text-gray-400">Assign users via Users tab</span>
              )}
            </div>
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------------

interface RecruiterTeamCardProps {
  readonly team: TeamDTO;
  readonly onEdit: (team: TeamDTO) => void;
  readonly onDelete: (team: TeamDTO) => void;
}

function RecruiterTeamCard({ team, onEdit, onDelete }: RecruiterTeamCardProps): React.JSX.Element {
  return (
    <div
      data-testid="team-card"
      data-team-id={team.id}
      className="mb-3 rounded-lg border border-border bg-white p-4"
    >
      <div className="mb-2 flex items-center justify-between">
        <h3 data-testid="team-card-name" className="font-bold text-text-strong">
          {team.name}
        </h3>
        <div className="flex gap-3">
          <button
            type="button"
            data-testid="team-card-edit"
            onClick={() => onEdit(team)}
            className="text-xs text-primary hover:underline"
            aria-label="Edit team"
            title="Edit team"
          >
            <i className="fa-solid fa-pen mr-1" aria-hidden />
            Edit
          </button>
          <button
            type="button"
            data-testid="team-card-delete"
            onClick={() => onDelete(team)}
            className="text-xs text-red-500 hover:underline"
            aria-label="Delete team"
            title="Delete team"
          >
            <i className="fa-solid fa-trash mr-1" aria-hidden />
            Delete
          </button>
        </div>
      </div>
      <div data-testid="team-card-lead" className="mb-2 text-xs text-gray-500">
        <span aria-hidden className="mr-1 text-amber-500" data-testid="team-card-lead-crown">
          ♛
        </span>
        Team Lead:{' '}
        <span data-testid="team-card-lead-name" className="font-medium text-slate-700">
          {teamLeadLabel(team.teamlead_name)}
        </span>
      </div>
      <div className="flex flex-wrap gap-2">
        {team.members.length > 0 ? (
          team.members.map((m) => (
            <span
              key={m.id}
              data-testid="team-member-chip"
              className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-600"
            >
              {m.name}
            </span>
          ))
        ) : (
          <span data-testid="team-no-members" className="text-xs text-gray-400">
            No members assigned
          </span>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab
// ---------------------------------------------------------------------------

/**
 * The Recruiter Teams admin tab. Lists recruiter teams as cards (server orders by
 * name) and offers Add / Edit / Delete (HARD). Empty list → "No teams configured".
 */
export function RecruiterTeamsTab(): React.JSX.Element {
  const query = useTeams();
  const deleteTeam = useDeleteTeam();
  const toast = useToast();
  const [editing, setEditing] = useState<TeamDTO | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const teams = query.data ?? [];

  function openAdd(): void {
    setEditing(null);
    setDialogOpen(true);
  }

  function openEdit(team: TeamDTO): void {
    setEditing(team);
    setDialogOpen(true);
  }

  function closeDialog(): void {
    setDialogOpen(false);
    setEditing(null);
  }

  function handleDelete(team: TeamDTO): void {
    deleteTeam.mutate(team.id, {
      onSuccess: () => toast.success('Team deleted'),
      onError: (error) => toast.error(errorMessage(error, 'Delete failed')),
    });
  }

  if (query.isLoading) {
    return (
      <div data-testid="teams-loading" className="py-4 text-center text-gray-400">
        Loading…
      </div>
    );
  }

  if (query.isError) {
    return (
      <div data-testid="teams-error" className="py-4 text-center text-red-500">
        {errorMessage(query.error, 'Failed to load teams')}
      </div>
    );
  }

  return (
    <div data-testid="teams-tab">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-medium text-text-strong">Recruiter Teams</h2>
        <button
          type="button"
          data-testid="teams-add"
          onClick={openAdd}
          className="rounded bg-primary px-3 py-1.5 text-xs font-medium text-white hover:opacity-90"
        >
          Add team
        </button>
      </div>

      {teams.length === 0 ? (
        <p data-testid="teams-empty" className="py-4 text-center text-sm text-gray-400">
          No teams configured
        </p>
      ) : (
        <div data-testid="teams-list">
          {teams.map((t) => (
            <RecruiterTeamCard key={t.id} team={t} onEdit={openEdit} onDelete={handleDelete} />
          ))}
        </div>
      )}

      {dialogOpen ? <RecruiterTeamDialog team={editing} onClose={closeDialog} /> : null}
    </div>
  );
}

export default RecruiterTeamsTab;
